import { Filter, ChevronsRight } from 'lucide-react';
import type { TabId, TabState } from '../types';
import { SUB_REGIONS } from '../utils/regions';
import { getFYOptions } from '../utils/filter';
import { useRequests } from '../context/RequestsContext';
interface Props {
  tab: TabId;
  state: TabState & {
    status?: string;
  };
  onUpdate: (updates: Partial<TabState>) => void;
}
function getLabel(tab: TabId, s: TabState & {
  status?: string;
}): string {
  let lbl = s.r;
  if (s.sub && s.sub !== 'All') lbl += ' › ' + s.sub;
  if (tab === 'vp' && s.status) lbl += ' · ' + s.status;
  const fyPart = s.fy !== 'All' ? s.fy : '';
  const qPart = s.q !== 'All' ? s.q : '';
  if (fyPart || qPart) lbl += ' · ' + (fyPart && qPart ? fyPart + '-' + qPart : fyPart || qPart);
  return lbl;
}
export function FilterBar({
  tab,
  state,
  onUpdate
}: Props) {
  const {
    requests
  } = useRequests();
  const fyOptions = getFYOptions(requests);
  const quarters = ['All', 'Q1', 'Q2', 'Q3', 'Q4'];
  const statuses = ['All', 'In Progress', 'Closed', 'On Hold', 'Cancelled'];
  const regions = ['Global', 'AMER', 'EMEA', 'JAPAC'];
  const subRegions = SUB_REGIONS[state.r] ?? [];
  function setRegion(r: string) {
    onUpdate({
      r,
      sub: 'All'
    });
  }
  return <div className="bg-white px-[18px] py-[10px] flex gap-4 flex-wrap items-start border-b border-[#e8eaf0] shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-id="element-127" data-mp="10dnfgsia0jjd">
      {/* Region */}
      <div className="flex flex-col gap-[6px]" data-id="element-128" data-mp="8n95h7ddvrcz">
        <div className="text-[9px] font-bold text-[#8892a0] uppercase tracking-[.06em]" data-id="element-129" data-mp="8o13spyd836y">Region</div>
        <div className="flex gap-1 flex-wrap" data-id="element-130" data-mp="3qirqqp1x7uq">
          {regions.map(r => <button key={r} className={`rpill ${state.r === r ? 'active' : ''}`} onClick={() => setRegion(r)} data-id="element-131" data-mp="hp8mw0sb8xgg">
              {r}
            </button>)}
        </div>
        {subRegions.length > 0 && <div className="flex items-center gap-[6px] pt-1 flex-wrap" data-id="element-132" data-mp="1t21glh4nvvz5">
            <span className="text-[9px] font-semibold text-[#8892a0] uppercase tracking-[.05em] whitespace-nowrap flex items-center gap-1" data-id="element-133" data-mp="1o3hqd4j1by0z">
              <ChevronsRight className="w-[10px] h-[10px] text-[#d0d5dd]" data-id="element-134" data-mp="lt7qi32m7vqt" />Sub-region
            </span>
            <div className="flex gap-1 flex-wrap" data-id="element-135" data-mp="qsastfr7nnv3">
              <button className={`srpill ${state.sub === 'All' ? 'active' : ''}`} onClick={() => onUpdate({
            sub: 'All'
          })} data-id="element-136" data-mp="ususor3655kx">
                All {state.r}
              </button>
              {subRegions.map(s => <button key={s} className={`srpill ${state.sub === s ? 'active' : ''}`} onClick={() => onUpdate({
            sub: s
          })} data-id="element-137" data-mp="7277530byhfw">
                  {s}
                </button>)}
            </div>
          </div>}
      </div>

      {/* Status (VP only) */}
      {tab === 'vp' && <div className="flex flex-col gap-[6px]" data-id="element-138" data-mp="3fhqx9t5pfjqq">
          <div className="text-[9px] font-bold text-[#8892a0] uppercase tracking-[.06em]" data-id="element-139" data-mp="3kglqzq57mspm">Status</div>
          <div className="flex gap-1 flex-wrap" data-id="element-140" data-mp="3cxuix7y83yfc">
            {statuses.map(s => <button key={s} className={`rpill ${(state.status ?? 'All') === s ? 'active' : ''}`} onClick={() => onUpdate({
          status: s
        })} data-id="element-141" data-mp="37yrg7u5pu4aj">
                {s}
              </button>)}
          </div>
        </div>}

      {/* Created Quarter */}
      <div className="flex flex-col gap-[6px]" data-id="element-142" data-mp="2tzryz7kj8roe">
        <div className="text-[9px] font-bold text-[#8892a0] uppercase tracking-[.06em]" data-id="element-143" data-mp="2u0jn5xbsvn4n">Created Quarter</div>
        <div className="flex items-center gap-2 flex-wrap" data-id="element-144" data-mp="2k8x2ladkobp8">
          <select className="fy-select" value={state.fy} onChange={e => onUpdate({
          fy: e.target.value
        })} data-id="element-145" data-mp="2k9ghsjbu8lzk">
            {fyOptions.map(y => <option key={y} value={y} data-id="element-146" data-mp="2b6xmqleandnv">{y === 'All' ? 'All FY' : 'FY' + y}</option>)}
          </select>
          <div className="w-px h-[18px] bg-[#e8eaf0] flex-shrink-0" data-id="element-147" data-mp="269xmws81f757" />
          {quarters.map(q => <button key={q} className={`cqfpill ${state.q === q ? 'active' : ''}`} onClick={() => onUpdate({
          q
        })} data-id="element-148" data-mp="a842dl9o6eqi">
              {q === 'All' ? 'All Q' : q}
            </button>)}
        </div>
      </div>

      {/* Summary label */}
      <div className="text-[10px] text-[#8892a0] ml-auto self-center flex items-center gap-1" data-id="element-149" data-mp="5d6jlmml8r0f">
        <Filter className="w-[11px] h-[11px]" data-id="element-150" data-mp="3mm3ag0w9pz7w" />
        {getLabel(tab, state)}
      </div>
    </div>;
}